import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  asId,
  type PileTypeId,
  type ProjectId,
  type StructuralPieceId,
} from '@obraplan/core/domain/shared/ids';
import {
  FLOOR_PLAN_KIND_LABEL,
  type FloorPlanKind,
} from '@obraplan/core/domain/takeoff/floor-plan';
import {
  pileLength,
  type PieceMark,
  type PileType,
  type StructuralPiece,
} from '@obraplan/core/domain/takeoff/pile';
import {
  nomeDaPeca,
  pecasImportadas,
  type QuantidadesPorPeca,
} from '@obraplan/core/domain/takeoff/planilha-por-peca';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { CorrecaoComMotivo } from './CorrecaoComMotivo';
import { NOME_DA_PECA_MAX, NomeInput } from './campos';
import {
  COR_DA_ESTACA,
  COR_DA_ESTACA_MARCADA,
  descricaoDoTipo,
  estacaMarcada,
  pavimentosSemDesenho,
  posicionadasDoTipo,
} from './estacas-da-prancha';
import { fatoresDaEstaca } from './fatores-proprios';
import { FatoresProprios } from './FatoresProprios';
import { metros } from './formato';
import { metresToPixel, type Pixel } from './plan-tracer';
import { TiposDeEstaca } from './TiposDeEstaca';
import type { AcaoDaEstaca, EstacaArrastada, EstacasNaPrancha } from './use-estacas-na-prancha';
import type { WorkspaceAction, WorkspaceDraft } from './typology-draft';

/**
 * A estaca sobre a prancha, o painel dela e o cadastro dos tipos (Fase 27, etapa 1b).
 *
 * O desenho é o círculo **no diâmetro do tipo**, com o nome dentro: o projeto de fundação
 * escreve a estaca assim, e é o que permite conferir na planta se a R60 não foi posta no
 * lugar da R40. As cores não seguem o tema, pelo mesmo motivo das do resto do traçado — a
 * prancha é papel branco.
 */

const SELECT_CLASS =
  'h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

// ── O desenho ───────────────────────────────────────────────────────────────────

export function EstacasDesenhadas({
  pecas,
  tipos,
  marcas,
  servicoMarcado,
  escala,
  zoom,
  selecionada,
  arrastando,
  novo,
  clicavel,
  onClicar,
}: {
  pecas: readonly StructuralPiece[];
  tipos: readonly PileType[];
  marcas: readonly PieceMark[];
  /** O serviço do modo Marcar. Ausente fora dele: aí só um anel diz que a estaca tem marca. */
  servicoMarcado: string | undefined;
  escala: number;
  zoom: number;
  selecionada: StructuralPieceId | undefined;
  arrastando: EstacaArrastada | undefined;
  /** O centro marcado e ainda não cadastrado, com o círculo do tipo escolhido. */
  novo: { readonly centro: Pixel; readonly tipo: PileType | undefined } | undefined;
  /** Ausente enquanto se traça outra coisa: aí o clique é do traço, e a estaca deixa passar. */
  clicavel: boolean;
  onClicar: (id: StructuralPieceId) => void;
}) {
  const tipoPorId = useMemo(() => new Map(tipos.map((tipo) => [tipo.id, tipo])), [tipos]);
  /** Sem tipo conhecido, o círculo ainda aparece — pequeno, para a estaca não sumir. */
  const raioDe = (tipo: PileType | undefined): number =>
    tipo === undefined ? 8 : Math.max(tipo.diameter / 2 / escala, 3);

  return (
    <g className={clicavel ? 'cursor-pointer' : 'pointer-events-none'}>
      {pecas.map((estaca) => {
        if (estaca.center === undefined || estaca.deletedAt !== undefined) return null;
        const tipo = tipoPorId.get(estaca.pileTypeId);
        const naMao = arrastando?.id === estaca.id && arrastando.moveu;
        const centro = naMao ? arrastando.atual : metresToPixel(estaca.center, escala);
        const raio = raioDe(tipo);
        const marcada =
          servicoMarcado !== undefined && estacaMarcada(marcas, estaca.id, servicoMarcado);
        const comMarca = marcas.some((marca) => marca.pieceId === estaca.id);
        const cor = marcada ? COR_DA_ESTACA_MARCADA : COR_DA_ESTACA;
        const escolhida = estaca.id === selecionada;
        const fonte = Math.min(Math.max(raio * 0.7, 9 / zoom), 14 / zoom);

        return (
          <g
            key={estaca.id}
            data-estaca={estaca.id}
            data-marcada={marcada ? 'true' : undefined}
            onClick={(evento) => {
              if (!clicavel) return;
              evento.stopPropagation();
              onClicar(estaca.id);
            }}
          >
            <title>
              {`Estaca ${estaca.name}${tipo === undefined ? '' : ` — ${tipo.name} · ${descricaoDoTipo(tipo)}`}`}
              {marcada ? ' — marcada' : ''}
            </title>
            {/* O anel pontilhado: a estaca tem marca de algum serviço, fora do modo Marcar. */}
            {comMarca && servicoMarcado === undefined && (
              <circle
                cx={centro.x}
                cy={centro.y}
                r={raio + 3 / zoom}
                fill="none"
                stroke={COR_DA_ESTACA_MARCADA}
                strokeWidth={1.5 / zoom}
                strokeDasharray={`${3 / zoom} ${2 / zoom}`}
              />
            )}
            <circle
              cx={centro.x}
              cy={centro.y}
              r={raio}
              fill={cor}
              fillOpacity={marcada ? 0.75 : 0.3}
              stroke={cor}
              strokeWidth={(escolhida ? 4 : marcada ? 2.5 : 1.5) / zoom}
            />
            <text
              x={centro.x}
              y={centro.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={fonte}
              fontWeight={600}
              fill={marcada ? '#ffffff' : '#422006'}
              stroke={marcada ? cor : '#ffffff'}
              strokeWidth={fonte * 0.22}
              paintOrder="stroke"
              className="pointer-events-none select-none"
            >
              {estaca.name}
            </text>
          </g>
        );
      })}
      {novo !== undefined && (
        <circle
          data-estaca-nova=""
          cx={novo.centro.x}
          cy={novo.centro.y}
          r={raioDe(novo.tipo)}
          fill="none"
          stroke={COR_DA_ESTACA}
          strokeWidth={2 / zoom}
          strokeDasharray={`${4 / zoom} ${3 / zoom}`}
          className="pointer-events-none"
        />
      )}
    </g>
  );
}

// ── O painel da ferramenta ──────────────────────────────────────────────────────

const ROTULO_DA_ACAO = { NOVA: 'Nova', POSICIONAR: 'Posicionar', MARCAR: 'Marcar' } as const;

export function EmEstaca({
  estacas,
  erro,
  tiposDaObra,
  onSair,
}: {
  estacas: EstacasNaPrancha;
  /** A última recusa do desenho — a estaca arrastada para onde não pode, por exemplo. */
  erro: string | undefined;
  /** O botão do cadastro de tipos, que quem monta o painel sabe preencher. */
  tiposDaObra: React.ReactNode;
  onSair: () => void;
}) {
  const { acao, fila, porTipo, proxima, tipos } = estacas;
  const acoes: readonly AcaoDaEstaca[] =
    fila.length > 0 ? ['NOVA', 'POSICIONAR', 'MARCAR'] : ['NOVA', 'MARCAR'];
  const tipoDaProxima = tipos.find((tipo) => tipo.id === proxima?.pileTypeId);

  return (
    <section
      aria-label="Estacas"
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">Estaca</h3>

      <div role="group" aria-label="O que fazer com a estaca" className="flex flex-wrap gap-1">
        {acoes.map((item) => (
          <Button
            key={item}
            size="sm"
            variant={acao === item ? 'primary' : 'ghost'}
            aria-pressed={acao === item}
            onClick={() => estacas.escolherAcao(item)}
          >
            {ROTULO_DA_ACAO[item]}
            {item === 'POSICIONAR' && ` (faltam ${String(fila.length)})`}
          </Button>
        ))}
      </div>

      {tipos.length === 0 && (
        <p className="text-xs text-(--color-content-muted)">
          Antes da primeira estaca, cadastre os tipos da obra — R40, R60 —: é do tipo que saem o
          diâmetro, o comprimento e o aço.
        </p>
      )}

      {acao === 'NOVA' && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-(--color-content-muted)">
            Clique no centro da estaca, escolha o tipo e o nome e adicione. A próxima já vem com o
            nome seguinte.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Tipo da estaca" htmlFor="estaca-tipo">
              <select
                id="estaca-tipo"
                className={SELECT_CLASS}
                value={estacas.tipoDaNova ?? ''}
                disabled={tipos.length === 0}
                onChange={(evento) => estacas.escolherTipo(asId<'PileType'>(evento.target.value))}
              >
                {tipos.map((tipo) => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.name} · {descricaoDoTipo(tipo)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Nome da estaca" htmlFor="estaca-nome">
              <Input
                id="estaca-nome"
                maxLength={NOME_DA_PECA_MAX}
                value={estacas.nome}
                className="w-32"
                onChange={(evento) => estacas.digitarNome(evento.target.value)}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter') estacas.adicionar();
                }}
              />
            </Field>
          </div>
          <span className="text-xs text-(--color-content-muted)">
            {estacas.centro === undefined ? 'Nenhum centro marcado na planta.' : 'Centro marcado.'}
          </span>
          {estacas.impedimento !== undefined && tipos.length > 0 && (
            <span className="text-xs text-(--color-state-warn)">{estacas.impedimento}</span>
          )}
          <div>
            <Button
              variant="primary"
              size="sm"
              disabled={estacas.centro === undefined || estacas.impedimento !== undefined}
              onClick={estacas.adicionar}
            >
              Adicionar estaca
            </Button>
          </div>
        </div>
      )}

      {acao === 'POSICIONAR' && proxima !== undefined && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">
            {`faltam ${porTipo.map((linha) => `${String(linha.quantas)} ${linha.tipo.name}`).join(' · ')}`}
          </p>
          <p className="text-xs">
            Próxima: <strong>{proxima.name}</strong>
            {tipoDaProxima === undefined ? '' : ` (${tipoDaProxima.name})`}. Clique no centro dela
            na planta.
          </p>
          <Field label="Estaca a posicionar" htmlFor="estaca-da-fila">
            <select
              id="estaca-da-fila"
              className={SELECT_CLASS}
              value={proxima.id}
              onChange={(evento) =>
                estacas.escolherDaFila(asId<'StructuralPiece'>(evento.target.value))
              }
            >
              {fila.map((estaca) => (
                <option key={estaca.id} value={estaca.id}>
                  {estaca.name} · {tipos.find((tipo) => tipo.id === estaca.pileTypeId)?.name ?? '—'}
                </option>
              ))}
            </select>
          </Field>
          <div>
            <Button variant="ghost" size="sm" disabled={fila.length < 2} onClick={estacas.pular}>
              Pular esta
            </Button>
          </div>
        </div>
      )}

      {acao === 'MARCAR' && (
        <div className="flex flex-col gap-2">
          {estacas.servicos.length === 0 ? (
            <p className="text-xs text-(--color-content-muted)">
              Nenhum serviço da obra conta estaca marcada. No cadastro do serviço — o teste de
              resistência, o controle tecnológico —, aplique-o à estaca e meça pelas estacas
              marcadas.
            </p>
          ) : (
            <>
              <p className="text-xs text-(--color-content-muted)">
                Clique numa estaca para marcá-la ou desmarcá-la neste serviço. Cada serviço tem as
                suas marcas.
              </p>
              <Field label="Serviço" htmlFor="estaca-servico">
                <select
                  id="estaca-servico"
                  className={SELECT_CLASS}
                  value={estacas.servico?.key ?? ''}
                  onChange={(evento) => estacas.escolherServico(evento.target.value)}
                >
                  {estacas.servicos.map((servico) => (
                    <option key={servico.key} value={servico.key}>
                      {servico.nome} · {servico.disciplina}
                    </option>
                  ))}
                </select>
              </Field>
              <span className="text-xs tabular-nums">
                {estacas.contagem === undefined
                  ? ''
                  : `${String(estacas.contagem.marcadas)} de ${String(estacas.contagem.posicionadas)} marcadas`}
              </span>
              <div className="flex flex-wrap items-end gap-2">
                <Button variant="secondary" size="sm" onClick={estacas.marcarTodas}>
                  Marcar todas
                </Button>
                <Field label="Só do tipo" htmlFor="estaca-filtro">
                  <select
                    id="estaca-filtro"
                    className={SELECT_CLASS}
                    value={estacas.filtro ?? ''}
                    onChange={(evento) =>
                      estacas.escolherFiltro(
                        evento.target.value === ''
                          ? undefined
                          : asId<'PileType'>(evento.target.value),
                      )
                    }
                  >
                    <option value="">Todos os tipos</option>
                    {tipos.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.name} ({String(posicionadasDoTipo(estacas.pecas, tipo.id))})
                      </option>
                    ))}
                  </select>
                </Field>
                <Button variant="ghost" size="sm" onClick={estacas.desmarcarTodas}>
                  Desmarcar todas
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-(--color-border-subtle) pt-2">
        {tiposDaObra}
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

// ── A estaca selecionada ────────────────────────────────────────────────────────

/**
 * Editar a estaca clicada: o nome, o tipo, o comprimento — este com motivo — e excluir.
 *
 * O comprimento do tipo vale até alguém corrigi-lo **nesta** estaca (RN-362), e a correção
 * fica à vista, com o motivo: um 14 m no lugar do 16 m do tipo, sem uma palavra ao lado, é
 * um número que alguém acaba desfazendo por achar que é erro.
 */
export function EstacaSelecionada({
  estaca,
  tipos,
  marcas,
  catalogo,
  pecasDaPlanilha,
  dispatch,
  onFechar,
}: {
  estaca: StructuralPiece;
  tipos: readonly PileType[];
  marcas: readonly PieceMark[];
  /** O cadastro da obra, para a lista de fatores (Fase 39, RN-511). Ausente, a lista não aparece. */
  catalogo?: ServiceCatalog | undefined;
  /** A planilha por peça da tipologia: a estaca de nome importado não leva fator (RN-514). */
  pecasDaPlanilha?: QuantidadesPorPeca | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  onFechar: () => void;
}) {
  const [corrigindo, setCorrigindo] = useState(false);
  const tipo = tipos.find((candidato) => candidato.id === estaca.pileTypeId);
  const quantasMarcas = marcas.filter((marca) => marca.pieceId === estaca.id).length;
  const daPlanilha = pecasImportadas(pecasDaPlanilha).has(nomeDaPeca(estaca.name));
  const fatores = useMemo(() => fatoresDaEstaca(catalogo, estaca, tipo), [catalogo, estaca, tipo]);

  return (
    <section
      aria-label={`Estaca ${estaca.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Estaca {estaca.name}</h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>
      {estaca.center === undefined && (
        <p className="text-xs text-(--color-state-warn)">
          Ainda sem posição: não conta no quantitativo até ser posicionada.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome" htmlFor={`estaca-${estaca.id}-nome`}>
          <NomeInput
            id={`estaca-${estaca.id}-nome`}
            value={estaca.name}
            className="w-32"
            respostaImediata
            onDescartar={() => dispatch({ type: 'DISMISS_ERROR' })}
            onCommit={(nome) => dispatch({ type: 'UPDATE_PILE', pieceId: estaca.id, name: nome })}
          />
        </Field>
        <Field label="Tipo" htmlFor={`estaca-${estaca.id}-tipo`}>
          <select
            id={`estaca-${estaca.id}-tipo`}
            className={SELECT_CLASS}
            value={estaca.pileTypeId}
            onChange={(evento) =>
              dispatch({
                type: 'UPDATE_PILE',
                pieceId: estaca.id,
                pileTypeId: asId<'PileType'>(evento.target.value),
              })
            }
          >
            {tipos.map((candidato) => (
              <option key={candidato.id} value={candidato.id}>
                {candidato.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {tipo !== undefined && (
        <div className="flex flex-col gap-1 text-xs">
          <div className="flex justify-between gap-3">
            <span className="text-(--color-content-muted)">Diâmetro</span>
            <strong className="tabular-nums">{metros(tipo.diameter * 100, 0)} cm</strong>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-(--color-content-muted)">
              Perfuração{estaca.lengthOverride === undefined ? ' (do tipo)' : ' (corrigida)'}
            </span>
            <strong className="tabular-nums">{metros(pileLength(estaca, tipo))} m</strong>
          </div>
          {estaca.lengthOverride !== undefined && (
            <p className="text-(--color-content-muted)">
              O tipo tem {metros(tipo.length)} m. Motivo: {estaca.lengthReason}
            </p>
          )}
          {quantasMarcas > 0 && (
            <p className="text-(--color-content-muted)">
              Marcada em {String(quantasMarcas)} {quantasMarcas === 1 ? 'serviço' : 'serviços'}.
            </p>
          )}
        </div>
      )}

      {tipo !== undefined &&
        (corrigindo ? (
          <CorrecaoComMotivo
            rotulo={`Comprimento da estaca ${estaca.name}`}
            inicial={pileLength(estaca, tipo)}
            {...(estaca.lengthReason === undefined ? {} : { motivoInicial: estaca.lengthReason })}
            unidade="m"
            onAplicar={(valor, motivo) => {
              dispatch({
                type: 'UPDATE_PILE',
                pieceId: estaca.id,
                lengthOverride: valor,
                lengthReason: motivo,
              });
              setCorrigindo(false);
            }}
            onCancelar={() => setCorrigindo(false)}
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setCorrigindo(true)}>
              Corrigir comprimento
            </Button>
            {estaca.lengthOverride !== undefined && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  dispatch({
                    type: 'UPDATE_PILE',
                    pieceId: estaca.id,
                    lengthOverride: tipo.length,
                  })
                }
              >
                Voltar ao do tipo
              </Button>
            )}
          </div>
        ))}

      {catalogo !== undefined &&
        (daPlanilha ? (
          <p className="text-xs text-(--color-content-muted)">
            A quantidade desta estaca vem pronta da planilha por peça: ela não leva fator próprio.
          </p>
        ) : (
          <FatoresProprios
            idBase={`estaca-${estaca.id}-fator`}
            titulo="Fatores desta estaca"
            servicos={fatores}
            rotuloDaVolta="Voltar ao do tipo"
            vazio="Nenhum serviço da obra mede a estaca com item a converter."
            onMudar={(serviceKey, itemKey, factor) =>
              dispatch({
                type: 'SET_PIECE_FACTOR',
                pieceId: estaca.id,
                serviceKey,
                itemKey,
                ...(factor === undefined ? {} : { factor }),
              })
            }
          />
        ))}

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_PILE', pieceId: estaca.id });
            onFechar();
          }}
        >
          <Trash2 className="size-4" /> Excluir estaca
        </Button>
      </div>
    </section>
  );
}

// ── Os tipos, a partir de qualquer lugar da planta ──────────────────────────────

/** O projeto dos tipos novos: o da página, ou o dos tipos que já existem. */
function projetoDosTipos(workspace: WorkspaceDraft, projectId: ProjectId | undefined): ProjectId {
  return projectId ?? workspace.pileTypes?.[0]?.projectId ?? asId<'Project'>('obra');
}

/** O botão que abre o cadastro de tipos, com as estacas de cada um contadas na bancada toda. */
export function TiposDaObra({
  workspace,
  projectId,
  dispatch,
}: {
  workspace: WorkspaceDraft;
  projectId: ProjectId | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const emUso = useMemo(() => {
    const contagem = new Map<PileTypeId, number>();
    for (const tipologia of workspace.typologies) {
      for (const estaca of tipologia.pieces ?? []) {
        if (estaca.deletedAt !== undefined) continue;
        contagem.set(estaca.pileTypeId, (contagem.get(estaca.pileTypeId) ?? 0) + 1);
      }
    }
    return contagem;
  }, [workspace.typologies]);

  return (
    <TiposDeEstaca
      tipos={workspace.pileTypes ?? []}
      emUso={emUso}
      projectId={projetoDosTipos(workspace, projectId)}
      onSalvar={(pileTypes) => dispatch({ type: 'SET_PILE_TYPES', pileTypes })}
      // A composição acha o item pelo catálogo, e os itens da referência que faltam entram
      // nele antes do tipo ser gravado (Fase 39, RN-505).
      catalogo={workspace.catalog}
      onAcrescentarItens={(acoes) => acoes.forEach((acao) => dispatch(acao))}
    />
  );
}

// ── "Desenhar este pavimento" (F4) ──────────────────────────────────────────────

/**
 * "Desenhar este pavimento": cria o desenho de Fundação ou Contenção que falta, com a área
 * comum onde a estaca mora (F3, F4).
 *
 * `faltam` vem de quem conhece a obra inteira — a página vê o pavimento que ainda não tem
 * desenho, e a bancada só vê os que já têm. Ausente, vale o que a bancada enxerga.
 */
export function DesenharPavimento({
  workspace,
  faltam: dePrimeira,
  dispatch,
  aoDesenhar,
}: {
  workspace: WorkspaceDraft;
  faltam: readonly FloorPlanKind[] | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  /**
   * Quem sabe criar o desenho no servidor. Ausente, a tipologia nasce na bancada — o caminho
   * de quem monta o quantitativo sem obra, que é como os testes o fazem.
   */
  aoDesenhar: ((kind: FloorPlanKind) => void) | undefined;
}) {
  const faltam = dePrimeira ?? pavimentosSemDesenho(workspace);
  if (faltam.length === 0) return null;

  return (
    <ul aria-label="Pavimentos sem desenho" className="flex w-full flex-col gap-2">
      {faltam.map((kind) => (
        <li key={kind} className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-(--color-content-muted)">
            {FLOOR_PLAN_KIND_LABEL[kind]} ainda não tem desenho — a estaca precisa de onde morar.
          </span>
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Desenhar este pavimento: ${FLOOR_PLAN_KIND_LABEL[kind]}`}
            onClick={() => {
              if (aoDesenhar !== undefined) {
                aoDesenhar(kind);
                return;
              }
              dispatch({
                type: 'ADD_TYPOLOGY',
                name: FLOOR_PLAN_KIND_LABEL[kind],
                kind: 'AREA_COMUM',
                floorKind: kind,
              });
            }}
          >
            Desenhar este pavimento
          </Button>
        </li>
      ))}
    </ul>
  );
}
